import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, Shield, Loader2 } from 'lucide-react';
import { tournaments as tournamentsApi } from '../lib/api';
import { toast } from 'sonner';

function CheckoutForm({ onSuccess, onCancel, tournamentName, amount }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/payment/success' },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 rounded-xl bg-[#0a0a0a] border border-[#333]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-[#888]">Inscription</span>
          <span className="text-lg font-bold text-white">{amount}€</span>
        </div>
        <p className="text-xs text-[#666] truncate">{tournamentName}</p>
      </div>

      <PaymentElement options={{ layout: 'tabs' }} />

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-3 rounded-xl border border-[#333] text-white font-medium hover:bg-[#1a1a1a] transition-colors">
          Annuler
        </button>
        <button type="submit" disabled={!stripe || loading}
          className="flex-1 py-3 rounded-xl bg-[#FF6B35] hover:bg-[#E55A25] text-white font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="confirm-payment-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
          {loading ? 'Paiement...' : 'Payer'}
        </button>
      </div>

      <p className="text-[10px] text-[#555] text-center flex items-center justify-center gap-1">
        <Shield className="w-3 h-3" /> Paiement sécurisé par Stripe
      </p>
    </form>
  );
}

export default function StripePaymentModal({ tournament, user, teamId, onClose, onSuccess }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const res = await tournamentsApi.checkoutTournament({
          tournament_id: tournament.id,
          origin_url: window.location.origin,
          team_id: teamId || null,
        });
        setClientSecret(res.data.client_secret);
        setPaymentIntentId(res.data.payment_intent_id);
        setPublishableKey(res.data.publishable_key);
        setStripePromise(loadStripe(res.data.publishable_key));
      } catch (e) {
        setError(e.response?.data?.detail || 'Erreur lors de la création du paiement');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handlePaymentSuccess = async (piId) => {
    try {
      await tournamentsApi.checkoutStatus(piId);
      toast.success('Inscription confirmée !');
      onSuccess?.();
      onClose();
    } catch {
      toast.success('Paiement accepté ! Inscription en cours...');
      onSuccess?.();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="stripe-payment-modal">
        <div className="flex items-center justify-between p-4 border-b border-[#333] shrink-0">
          <h2 className="text-lg font-bold text-white">Paiement</h2>
          <button onClick={onClose} className="text-[#666] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={onClose} className="text-[#888] text-sm hover:text-white">Fermer</button>
            </div>
          ) : clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{
              clientSecret,
              appearance: {
                theme: 'night',
                variables: { colorPrimary: '#FF6B35', colorBackground: '#0a0a0a', colorText: '#ffffff', borderRadius: '12px' },
              },
            }}>
              <CheckoutForm
                onSuccess={handlePaymentSuccess}
                onCancel={onClose}
                tournamentName={tournament.name}
                amount={tournament.price}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
}
